import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import { surahs } from '../../data/quranData';
import { duas, duaCategories } from '../../data/duasData';

const {
    FiEdit3, FiHeart, FiTrendingUp, FiTrash2,
    FiList, FiPlus, FiX, FiChevronDown, FiChevronUp, FiMinus,
    FiBook, FiMessageSquare, FiSun, FiFolder
} = FiIcons;

const ProgressTab = () => {
    const { user, createPlaylist, deletePlaylist, addToPlaylist, removeFromPlaylist, deleteCategory, removeItemFromCategory } = useAuth();
    const [activeTab, setActiveTab] = useState('favorites');
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [showNewPlaylistInput, setShowNewPlaylistInput] = useState(false);
    const [expandedPlaylistId, setExpandedPlaylistId] = useState(null);
    const [selectedSurahToAdd, setSelectedSurahToAdd] = useState('');

    const getDuaCategoryName = (categoryId) => {
        return (
            duaCategories.find(cat => cat.id === categoryId)?.name || 'Dua'
        );
    };

    // Category State
    const [expandedCategoryId, setExpandedCategoryId] = useState(null);

    const tabs = [
        { id: 'favorites', name: 'Favorites', icon: FiHeart },
        { id: 'notes', name: 'Notes', icon: FiEdit3 },
        { id: 'playlists', name: 'Playlists', icon: FiList },
        { id: 'categories', name: 'Categories', icon: FiFolder },
        { id: 'progress', name: 'Progress', icon: FiTrendingUp }
    ];

    const [surahCache, setSurahCache] = useState({});
    const [loadingSurahs, setLoadingSurahs] = useState(false);
    

    const notes = user?.progress?.notes || [];
    const favorites = user?.progress?.favorites || [];
    const playlists = user?.playlists || [];
    const categories = user?.categories || [];
    const quranProgress = user?.progress?.quran || { clicked: [], read: [], listened: [] };
    const hadithProgress = user?.progress?.hadith || { completed: [], favorites: [], studied: [] };
    const duaProgress = user?.progress?.duas || { completed: [], favorites: [] };
    // const readingProgress = user?.progress?.readingProgress || { quran: 0, hadith: 0, duas: 0 };
    
    const [hadithCache, setHadithCache] = useState({});
    const [loadingHadiths, setLoadingHadiths] = useState(false);

    const parseHadithFavorite = (id) => {
        const [book, chapter, hadith] = id.split('-');
        return {
            book,
            chapter: Number(chapter),
            hadith: Number(hadith),
        };
    };

    const fetchFavoriteHadiths = async () => {
        const hadithFavorites = favorites.filter(f => f.type === 'hadith');
        if (!hadithFavorites.length) return;

        setLoadingHadiths(true);

        try {
            const uniqueChapters = [
                ...new Set(
                    hadithFavorites.map(f => {
                        const { book, chapter } = parseHadithFavorite(f.id);
                        return `${book}-${chapter}`;
                    })
                )
            ];

            const requests = uniqueChapters.map(async (key) => {
                const [book, chapter] = key.split('-');

                const arabicUrl = `https://cdn.jsdelivr.net/gh/fawazahmed0/hadith-api@1/editions/ara-${book}/sections/${chapter}.json`;
                const arabicRes = await fetch(arabicUrl);
                const arabicData = await arabicRes.json();

                let englishData = { hadiths: [] };
                try {
                    const engRes = await fetch(
                        `https://cdn.jsdelivr.net/gh/fawazahmed0/hadith-api@1/editions/eng-${book}/sections/${chapter}.json`
                    );
                    if (engRes.ok) {
                        englishData = await engRes.json();
                    }
                } catch (error) {
                    console.log(error);
                    
                }

                const merged = arabicData.hadiths.map((h, index) => {
                    const trans =
                        englishData.hadiths.find(t => t.hadithnumber === h.hadithnumber) ||
                        englishData.hadiths[index] ||
                        {};

                    return {
                        hadithNumber: h.hadithnumber,
                        arabic: h.text,
                        english: trans.text || '',
                        grade: h.grades?.[0]?.grade || ''
                    };
                });

                return { key, hadiths: merged };
            });

            const results = await Promise.all(requests);

            const cache = {};
            results.forEach(r => {
                cache[r.key] = r.hadiths;
            });

            setHadithCache(cache);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load hadith favorites');
        } finally {
            setLoadingHadiths(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'favorites') {
            fetchFavoriteSurahs();
            fetchFavoriteHadiths();
        }
    }, [activeTab, favorites, fetchFavoriteHadiths]);

    const parseQuranFavorite = (id) => {
        const [surah, ayah] = id.split('-').map(Number);
        return { surah, ayah };
    };

    const fetchFavoriteSurahs = async () => {
        const quranFavorites = favorites.filter(f => f.type === 'quran');
        if (!quranFavorites.length) return;

        setLoadingSurahs(true);

        try {
            const uniqueSurahs = [
                ...new Set(quranFavorites.map(f => parseQuranFavorite(f.id).surah))
            ];

            const requests = uniqueSurahs.map(async (surah) => {
                const res = await fetch(`https://api.alquran.cloud/v1/surah/${surah}`);
                const json = await res.json();
                return json.data;
            });

            const results = await Promise.all(requests);

            const cache = {};
            results.forEach(surah => {
                cache[surah.number] = surah;
            });

            setSurahCache(cache);
        } catch (err) {
            toast.error('Failed to load favorite ayahs');
            console.log(err);
            
        } finally {
            setLoadingSurahs(false);
        }
    };
    

    const handleCreatePlaylist = () => {
        if (!newPlaylistName.trim()) {
            toast.error('Playlist name cannot be empty');
            return;
        }
        createPlaylist(newPlaylistName);
        setNewPlaylistName('');
        setShowNewPlaylistInput(false);
        toast.success('Playlist created!');
    };

    const handleAddSurahToPlaylist = (playlistId) => {
        if (!selectedSurahToAdd) return;
        addToPlaylist(playlistId, parseInt(selectedSurahToAdd));
        setSelectedSurahToAdd('');
        toast.success('Surah added to playlist');
    };

    const renderCategories = () => (
        <div className="space-y-4">
            {categories.length === 0 ? (
                <div className="text-center py-12">
                    <SafeIcon icon={FiFolder} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No categories created yet</p>
                </div>
            ) : (
                categories.map((category) => (
                    <motion.div
                        key={category.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                    >
                        <div className="p-4 flex justify-between items-center">
                            <div
                                className="flex-1 cursor-pointer flex items-center gap-2"
                                onClick={() => setExpandedCategoryId(expandedCategoryId === category.id ? null : category.id)}
                            >
                                <SafeIcon
                                    icon={expandedCategoryId === category.id ? FiChevronUp : FiChevronDown}
                                    className="w-4 h-4 text-gray-500"
                                />
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white">{category.name}</h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{category.items?.length || 0} Items</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    if (window.confirm('Delete category?')) deleteCategory(category.id);
                                }}
                                className="text-gray-400 hover:text-red-500 transition-colors p-2"
                            >
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                        </div>

                        <AnimatePresence>
                            {expandedCategoryId === category.id && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="border-t border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800/50"
                                >
                                    <div className="p-4 space-y-2">
                                        {(category.items || []).length === 0 ? (
                                            <p className="text-sm text-gray-500 text-center py-2">No items in this category.</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {category.items.map((item, idx) => (
                                                    <div key={idx} className="flex justify-between items-start px-3 py-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                                        <div>
                                                            <span className={`text-xs uppercase font-bold block ${item.item_type === 'hadith' ? 'text-blue-500' :
                                                                    item.item_type === 'dua' ? 'text-purple-500' :
                                                                        item.item_type === 'book' ? 'text-amber-500' :
                                                                            'text-emerald-500'
                                                                }`}>{item.item_type}</span>
                                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 block">{item.reference}</span>
                                                            <span className="text-xs text-gray-500 truncate block max-w-xs">{item.content}</span>
                                                        </div>
                                                        <button
                                                            onClick={() => removeItemFromCategory(category.id, item.id)}
                                                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                                            title="Remove from category"
                                                        >
                                                            <SafeIcon icon={FiMinus} className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                ))
            )}
        </div>
    );

    const renderProgressCard = (title, icon, items) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <SafeIcon icon={icon} className="text-emerald-600" />
                {title}
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
                {items.map((item, index) => (
                    <motion.div
                        key={item.label}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center"
                    >
                        <div className={`w-16 h-16 bg-${item.color}-100 dark:bg-${item.color}-900 rounded-full flex items-center justify-center mx-auto mb-4`}>
                            <span className={`text-2xl font-bold text-${item.color}-600`}>
                                {item.value}
                                {item.isPercent && '%'}
                            </span>
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                            {item.label}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            {item.count !== undefined ? `${item.count} / ${item.total || 100}` : (item.isPercent ? `${item.value}% Complete` : `${item.value} Items`)}
                        </p>
                        {item.isPercent && (
                            <div className={`w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2`}>
                                <div
                                    className={`bg-${item.color}-600 h-2 rounded-full transition-all duration-500`}
                                    style={{ width: `${item.value}%` }}
                                ></div>
                            </div>
                        )}
                    </motion.div>
                ))}
            </div>
        </div>
    );

    const renderProgress = () => (
        <div className="space-y-8">
            {/* Quran Progress */}
            {renderProgressCard('Quran Progress', FiBook, [
                { label: 'Completed', value: Math.round((quranProgress.read?.length || 0) / 114 * 100), color: 'emerald', count: quranProgress.read?.length || 0, total: 114, isPercent: true },
                { label: 'Listened', value: Math.round((quranProgress.listened?.length || 0) / 114 * 100), color: 'blue', count: quranProgress.listened?.length || 0, total: 114, isPercent: true },
                { label: 'Opened', value: Math.round((quranProgress.clicked?.length || 0) / 114 * 100), color: 'indigo', count: quranProgress.clicked?.length || 0, total: 114, isPercent: true }
            ])}

            {/* Hadith Progress */}
            {renderProgressCard('Hadith Progress', FiMessageSquare, [
                { label: 'Studied', value: hadithProgress.completed?.length || 0, color: 'emerald', isPercent: false },
                { label: 'Favorites', value: hadithProgress.favorites?.length || 0, color: 'blue', isPercent: false },
                { label: 'Completed', value: hadithProgress.studied?.length || 0, color: 'indigo', isPercent: false }
            ])}

            {/* Dua Progress */}
            {renderProgressCard('Dua Progress', FiSun, [
                { label: 'Completed', value: duaProgress.completed?.length || 0, color: 'emerald', isPercent: false },
                { label: 'Favorites', value: duaProgress.favorites?.length || 0, color: 'blue', isPercent: false },
                { label: 'Memorized', value: 0, color: 'indigo', isPercent: false }
            ])}
        </div>
    );

    const renderNotes = () => (
        <div className="space-y-4">
            {notes.length === 0 ? (
                <div className="text-center py-12">
                    <SafeIcon icon={FiEdit3} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No notes yet</p>
                </div>
            ) : (
                notes.map((note, index) => (
                    <motion.div
                        key={note.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                    <span
                                        className={`px-2 py-1 text-xs rounded-full uppercase font-bold tracking-wide ${
                                            note.reference_type === 'quran'
                                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                                            : note.reference_type === 'hadith'
                                            ? 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200'
                                            : note.reference_type === 'dua'
                                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                            : 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200'
                                        }`}
                                        >
                                        {note.reference_type}
                                    </span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {note.reference}
                                    </span>
                                </div>
                                <p className="text-gray-900 dark:text-gray-100 mb-2">
                                    {note.text}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(note.created_at).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                ))
            )}
        </div>
    );

    const renderFavorites = () => (
        <div className="space-y-1">
            {favorites.length === 0 && (
                <div className="text-center py-12">
                    <SafeIcon icon={FiHeart} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No favorites yet</p>
                </div>
            )}

            {loadingSurahs && (
                <p className="text-sm text-center text-gray-500">Loading Quran...</p>
            )}

            {favorites.map((favorite, index) => {
                // Handle Quran favorites
                if (favorite.type === 'quran') {
                    const { surah, ayah } = parseQuranFavorite(favorite.id);
                    const surahData = surahCache[surah];
                    const ayahData = surahData?.ayahs?.find(a => a.numberInSurah === ayah);

                    return (
                        <motion.div
                            key={favorite.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2"
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    {/* Header */}
                                    <div className="flex items-center space-x-2">
                                        <span className="px-2 py-1 text-xs font-bold uppercase rounded-full bg-red-100 text-red-700">
                                            Quran
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {surahData
                                                ? `${surahData.englishName} (${surah}:${ayah})`
                                                : `Surah ${surah}:${ayah}`}
                                        </span>
                                    </div>

                                    {/* Ayah Text */}
                                    {ayahData ? (
                                        <p className="font-arabic text-right text-xl leading-loose text-gray-900 dark:text-gray-100">
                                            {ayahData.text}
                                        </p>
                                    ) : (
                                        <p className="text-sm text-gray-500">
                                            Loading ayah...
                                        </p>
                                    )}

                                    <p className="text-xs text-gray-500 mt-2">
                                        {new Date(favorite.date).toLocaleDateString()}
                                    </p>
                                </div>

                                <SafeIcon
                                    icon={FiHeart}
                                    className="w-4 h-4 text-red-500 fill-current"
                                />
                            </div>
                        </motion.div>
                    );
                }

                // Handle Hadith favorites
                if (favorite.type === 'hadith') {
                    const { book, chapter, hadith } = parseHadithFavorite(favorite.id);
                    const chapterHadiths = hadithCache[`${book}-${chapter}`];
                    const hadithData = chapterHadiths?.find(
                        h => Number(h.hadithNumber) === hadith
                    );

                    return (
                        <motion.div
                            key={favorite.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3"
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    {/* Header */}
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="px-2 py-1 text-xs font-bold uppercase rounded-full bg-yellow-100 text-yellow-700">
                                            Hadith
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {book.toUpperCase()} • {chapter}:{hadith}
                                        </span>
                                        {hadithData?.grade && (
                                            <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                                {hadithData.grade}
                                            </span>
                                        )}
                                    </div>

                                    {/* Arabic */}
                                    {hadithData ? (
                                        <>
                                            <p className="font-arabic text-right text-lg leading-loose text-gray-900 dark:text-gray-100">
                                                {hadithData.arabic}
                                            </p>

                                            {/* {hadithData.english && (
                                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">
                                                    {hadithData.english}
                                                </p>
                                            )} */}
                                        </>
                                    ) : (
                                        <p className="text-sm text-gray-500">Loading hadith...</p>
                                    )}

                                    <p className="text-xs text-gray-500 mt-2">
                                        {new Date(favorite.date).toLocaleDateString()}
                                    </p>
                                </div>

                                <SafeIcon
                                    icon={FiHeart}
                                    className="w-4 h-4 text-red-500 fill-current"
                                />
                            </div>
                        </motion.div>
                    );
                }

                // Handle Dua favorites
                if (favorite.type === 'dua') {
                    const duaId = Number(favorite.id.replace('dua-', ''));
                    const dua = duas.find(d => d.id === duaId);

                    return (
                        <motion.div
                            key={`dua-${favorite.id}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                        >
                            <div className="flex justify-between gap-4">
                                <div className="flex-1">
                                    {/* Header */}
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="px-2 py-1 text-xs font-bold uppercase rounded-full bg-purple-100 text-purple-700">
                                            Dua
                                        </span>
                                        {dua?.category && (
                                            <span className="text-sm text-gray-500">
                                                {getDuaCategoryName(dua.category)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Title */}
                                    {dua?.title && (
                                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                            {dua.title}
                                        </h3>
                                    )}

                                    {/* Arabic */}
                                    {dua ? (
                                        <>
                                            <p className="font-arabic text-right text-xl leading-loose text-gray-900 dark:text-gray-100">
                                                {dua.arabic}
                                            </p>

                                            {/* English */}
                                            {dua.translations?.english && (
                                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">
                                                    {dua.translations.english}
                                                </p>
                                            )}

                                            {/* Reference */}
                                            {dua.reference && (
                                                <p className="text-xs text-gray-500 mt-2">
                                                    Reference: {dua.reference}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <p className="text-sm text-gray-500">
                                            Dua not found
                                        </p>
                                    )}

                                    {/* Date */}
                                    <p className="text-xs text-gray-500 mt-2">
                                        Saved on {new Date(favorite.date).toLocaleDateString()}
                                    </p>
                                </div>

                                <SafeIcon
                                    icon={FiHeart}
                                    className="w-4 h-4 text-red-500 fill-current mt-1"
                                />
                            </div>
                        </motion.div>
                    );
                }

                // Handle Book favorites
                if (favorite.type === 'book') {
                    return (
                        <motion.div
                            key={favorite.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2"
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    {/* Header */}
                                    <div className="flex items-center space-x-2">
                                        <span className="px-2 py-1 text-xs font-bold uppercase rounded-full bg-yellow-100 text-yellow-700">
                                            Book
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {favorite.id.replace(/-/g, ' ')}
                                        </span>
                                    </div>

                                    {/* Placeholder for Hadith text (you can fetch later) */}
                                    <p className="text-sm text-gray-700 dark:text-gray-200">
                                        {favorite.id}
                                    </p>

                                    <p className="text-xs text-gray-500 mt-2">
                                        {new Date(favorite.date).toLocaleDateString()}
                                    </p>
                                </div>

                                <SafeIcon
                                    icon={FiHeart}
                                    className="w-4 h-4 text-red-500 fill-current"
                                />
                            </div>
                        </motion.div>
                    );
                }

                return null; // Skip unknown types
            })}
        </div>
    );

    const renderPlaylists = () => (
        <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">My Playlists</h3>
                <button
                    onClick={() => setShowNewPlaylistInput(true)}
                    className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    Create New
                </button>
            </div>

            {showNewPlaylistInput && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4"
                >
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newPlaylistName}
                            onChange={(e) => setNewPlaylistName(e.target.value)}
                            maxLength={15}
                            placeholder="Playlist name (max 15 chars)"
                            className="flex-1 px-3 py-2 bg-white dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        />
                        <button
                            onClick={handleCreatePlaylist}
                            className="px-4 py-2 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700"
                        >
                            Save
                        </button>
                        <button
                            onClick={() => setShowNewPlaylistInput(false)}
                            className="px-2 text-gray-500 hover:text-gray-700"
                        >
                            <SafeIcon icon={FiX} className="w-5 h-5" />
                        </button>
                    </div>
                </motion.div>
            )}

            {playlists.length === 0 ? (
                <div className="text-center py-12">
                    <SafeIcon icon={FiList} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No playlists yet</p>
                </div>
            ) : (
                playlists.map((playlist, index) => (
                    <motion.div
                        key={playlist.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                    >
                        <div className="p-4 flex justify-between items-center">
                            <div
                                className="flex-1 cursor-pointer flex items-center gap-2"
                                onClick={() => setExpandedPlaylistId(expandedPlaylistId === playlist.id ? null : playlist.id)}
                            >
                                <SafeIcon
                                    icon={expandedPlaylistId === playlist.id ? FiChevronUp : FiChevronDown}
                                    className="w-4 h-4 text-gray-500"
                                />
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white">{playlist.name}</h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{playlist?.surahs?.length} Surahs</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    if (window.confirm('Delete playlist?')) deletePlaylist(playlist.id);
                                }}
                                className="text-gray-400 hover:text-red-500 transition-colors p-2"
                            >
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                        </div>

                        <AnimatePresence>
                            {expandedPlaylistId === playlist.id && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="border-t border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800/50"
                                >
                                    <div className="p-4 space-y-2">
                                        {/* Add Surah Section */}
                                        <div className="flex gap-2 mb-4">
                                            <select
                                                value={selectedSurahToAdd}
                                                onChange={(e) => setSelectedSurahToAdd(e.target.value)}
                                                className="flex-1 px-3 py-2 bg-white dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                            >
                                                <option value="">Select Surah to Add...</option>
                                                {surahs.map(s => (
                                                    <option key={s.number} value={s.number}>
                                                        {s.number}. {s.name} - {s.englishName}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={() => handleAddSurahToPlaylist(playlist.id)}
                                                disabled={!selectedSurahToAdd}
                                                className="px-3 py-2 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {/* Surah List */}
                                        {playlist.surahs.length === 0 ? (
                                            <p className="text-sm text-gray-500 text-center py-2">No surahs in this playlist.</p>
                                        ) : (
                                            <div className="space-y-1">
                                                {playlist.surahs.map((surahId) => {
                                                    const surahData = surahs.find(s => s.number === surahId);
                                                    return (
                                                        <div key={surahId} className="flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                                {surahId}. {surahData ? surahData.name : 'Unknown Surah'}
                                                            </span>
                                                            <button
                                                                onClick={() => removeFromPlaylist(playlist.id, surahId)}
                                                                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                                                title="Remove from playlist"
                                                            >
                                                                <SafeIcon icon={FiMinus} className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                ))
            )}
        </div>
    );

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <div className="flex overflow-x-auto custom-scrollbar">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center space-x-2 px-6 py-4 font-medium whitespace-nowrap transition-colors ${activeTab === tab.id
                                    ? 'text-emerald-600 border-b-2 border-emerald-600'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                }`}
                        >
                            <SafeIcon icon={tab.icon} className="w-4 h-4" />
                            <span>{tab.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            <div className="p-4">
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    {activeTab === 'notes' && renderNotes()}
                    {activeTab === 'favorites' && renderFavorites()}
                    {activeTab === 'playlists' && renderPlaylists()}
                    {activeTab === 'categories' && renderCategories()}
                    {activeTab === 'progress' && renderProgress()}
                </motion.div>
            </div>
        </div>
    );
};

export default ProgressTab;